import { Component, HostBinding, Input } from '@angular/core';

import { Metar } from '../../weather/models/metar.model';
import { WeatherService } from '../../weather/weather/weather.service';
import { LatLon } from '../../util/geodesy/latlon';
import { PromiseState, StatefulPromise } from '../../util/stateful-promise';
import { MatDialog } from '@angular/material';
import { WeatherLegendComponent } from '../../shared/weather-legend/weather-legend.component';


@Component({
  selector: 'app-metar-section',
  templateUrl: './metar-section.component.html',
  styleUrls: ['./metar-section.component.scss']
})
export class MetarSectionComponent {
  private _latlon: LatLon;
  metarPromise: StatefulPromise<Metar[]>;

  @HostBinding('class.wx-section') _clsWxSection = true;

  /**
   * Sets the initial state of the Translated toggle in the header.
   */
  @Input() translated: boolean;

  /**
   * Sets the airport identifier. The METAR whose station identifier matches this identifier will be shown in the
   * top section of this component.
   */
  @Input() identifier: string;

  /**
   * Returns the latlon of the primary station identifier
   */
  get latlon(): LatLon {
    return this._latlon;
  }

  /**
   * Sets the lat/lon to be used as the center point when querying METARs. This is required (all METAR data, including
   * for the primary identifier, is loaded by lat/lon).
   */
  @Input() set latlon(value: LatLon) {
    if (this._latlon !== value) {
      this._latlon = value;
      this._load();
    }
  }

  constructor(
    private _wx: WeatherService,
    public _dialog: MatDialog
  ) { }

  private _load() {
    this.metarPromise = new StatefulPromise(this._wx.getMetarsByLatLon(this._latlon));
  }

  private get _metars(): Array<Metar> {
    if (!this.metarPromise || this.metarPromise.state !== PromiseState.Fulfilled) {
      return null;
    }
    return this.metarPromise.value;
  }

  /**
   * Returns the primary METAR
   * @returns {Metar}
   */
  get primaryMetar(): Metar {
    // this selects the METAR for the current airport, if there is one
    // it intentionally checks again after prepending a K to the identifier to account for fake ICAO ADDS airports (like M91)
    let tempMetar: Metar = null;
    if (this._metars) {
      tempMetar = this._metars.find(metar => metar.stationId === this.identifier);
      if (!tempMetar) {
        tempMetar = this._metars.find(metar => metar.stationId === 'K' + this.identifier);
      }
    }
    return tempMetar;
  }

  /**
   * Returns nearby METARs (excluding the primary METAR)
   * @returns {Array<Metar>}
   */
  get nearbyMetars(): Array<Metar> {
    // this returns the nearby METARs by filtering out a local METAR, if there is one
    // it intentionally checks twice prepending a K to the identifier the second time to account for fake ICAO ADDS airports (like M91)
    return (this._metars || []).filter(metar => metar.stationId !== this.identifier && metar.stationId !== 'K' + this.identifier);
  }

  openWeatherLegend() {
    const dialogRef = this._dialog.open(WeatherLegendComponent, {
      // height: '500px'
    });
  }
}
